import { LitElement, html } from './-global.min.js'

class HHProductCardDs extends LitElement {
  static properties = {
    productUrl: {
      type: String,
      attribute: 'product-url'
    },
    currentVariantData: {
      type: Object,
    },
    currentVariantSupplementData: {
      type: Object,
    },
    options: {
      type: Array
    },
    collapseOpenState: {
      type: Boolean,
    }
  }

  constructor() {
    super()
    this.productUrl = ''
    this.options = []
    this.currentVariantData = {}
    this.currentVariantSupplementData = {}
    this.collapseOpenState = false
  }

  get fieldset() {
    return this.querySelector('.js-card-fieldset')
  }

  get variantData() {
    return JSON.parse(this.querySelector('.js-card-variant-data').textContent)
  }

  get variantSupplementData() {
    return JSON.parse(this.querySelector('.js-card-variant-supplement-data').textContent)
  }

  get anchor() {
    return this.querySelector('.js-anchor')
  }

  get variantMedias() {
    return this.querySelectorAll('.js-variant-media')
  }

  get formIdInput() {
    return this.querySelector('.js-quick-buy-id')
  }

  get quickShopButton() {
    return this.querySelector('.js-card-quick-shop-button')
  }

  get preorderText() {
    return this.dataset.preorderText || ''
  }

  get preorderPropertyInputs() {
    return [...this.querySelectorAll('.js-preorder-property')]
  }

  get preorderMessages() {
    return [...this.querySelectorAll('.js-preorder-message')]
  }

  get badge() {
    return this.querySelector('.js-card-badge')
  }

  get colorSwatchLabel() {
    return this.querySelector('.js-color-selected-value')
  }

  get cardPriceWrappers() {
    return this.querySelectorAll('.js-price-wrapper')
  }

  get cardPrices() {
    return this.querySelectorAll('.js-price')
  }

  get cardComparePrices() {
    return this.querySelectorAll('.js-compare-price')
  }

  get collapse() {
    return this.querySelector('.js-card-collapse')
  }

  get collapseOpenButton() {
    return this.querySelector('.js-card-collapse-open')
  }

  get collapseCloseButton() {
    return this.querySelector('.js-card-collapse-close')
  }

  get cardInfo() {
    return this.querySelector('.js-card-info')
  }

  connectedCallback() {
    super.connectedCallback()
    
    // Select first available option by default
    if (this.fieldset) {
      this.selectFirstAvailableOption();
      this.fieldset.addEventListener('change', this.onChangeHandler.bind(this))
    }

    if (this.collapse) {
      if (this.collapseOpenButton) this.collapseOpenButton.addEventListener('click', () => this.collapseOpen())
      if (this.collapseCloseButton) this.collapseCloseButton.addEventListener('click', () => this.collapseClose())
      this.addEventListener('mouseenter', () => this.handleDesktopOpen())
      this.addEventListener('focusin', () => this.handleDesktopOpen())
      window.addEventListener('hh-ds-collapse-open', this.handleCollapseEvent.bind(this));
      window.addEventListener('resize', () => this.queryCollapseHeight());
      window.addEventListener('resize', () => this.queryCardInfoHeight());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this.fieldset) this.fieldset.removeEventListener('change', this.onChangeHandler)
    if (this.collapse) {
      if (this.collapseOpenButton) this.collapseOpenButton.removeEventListener('click', () => this.collapseOpen())
      if (this.collapseCloseButton) this.collapseCloseButton.removeEventListener('click', () => this.collapseClose())
      this.removeEventListener('mouseenter', () => this.handleDesktopOpen())
      this.removeEventListener('focusin', () => this.handleDesktopOpen())
      window.removeEventListener('hh-ds-collapse-open', this.handleCollapseEvent);
      window.removeEventListener('resize', () => this.queryCollapseHeight());
      window.removeEventListener('resize', () => this.queryCardInfoHeight());
    }
  }


  firstUpdated() {
    if (this.collapse) this.queryCollapseHeight()
    if (this.cardInfo) this.queryCardInfoHeight()
  }

  // Variant switching update START
  onChangeHandler(event) {
    this.querySelectedOptions()
    this.querySelectedVariant()
    this.updateURL()
    this.updateActiveMedia()
    this.updateFormId()
    this.updateQuickShopButton()
    this.updateBadge()
    this.updateColorSwatchLabel(event)
    this.updateCardPrice()
  }

  querySelectedOptions() {
    const arrayFieldset = Array.from(this.querySelectorAll('fieldset'))
    this.options = arrayFieldset.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find(
        (radio) => radio.checked
      ).value
    })
  }

  querySelectedVariant() {
    if (!this.variantData || !Array.isArray(this.variantData)) {
      console.error('Variant data is not available or invalid.');
      return;
    }

    const selectedVariant = this.variantData.find((variant) => {
      return !variant.options
      .map((option, index) => this.options[index] === option)
      .includes(false);
    })

    if (window.sale.enabled && !(selectedVariant?.product_tags?.includes('no-sale') || selectedVariant?.product_tags?.includes('last-chance'))) {
      selectedVariant.compare_at_price = selectedVariant.price
      selectedVariant.price = Math.ceil(selectedVariant.price * (1 - window.sale.savePercentage / 100))
    }

    this.currentVariantData = selectedVariant

    if (!this.currentVariantData) {
      console.warn('No matching variant found.');
      return;
    }

    this.currentVariantSupplementData = this.variantSupplementData?.find(
      (variant) => variant.id === this.currentVariantData.id
    )
  }

  updateURL() {
    if (this.currentVariantSupplementData && this.anchor) {
      this.anchor.href = this.currentVariantSupplementData.url
    }
  }

  updateActiveMedia() {
    for (const media of this.variantMedias) {
      media.classList.remove('active')
    }

    const selectedVariantMedia = Array.from(this.variantMedias).find((media) => {
      return Number(media.dataset.variantId) === Number(this.currentVariantData.id)
    })
    selectedVariantMedia?.classList.add('active')
  }

  updateFormId() {
    if (this.currentVariantData && this.formIdInput) {
      this.formIdInput.value = this.currentVariantData.id
    }
  }

  updateQuickShopButton() {
    if (this.currentVariantData && this.quickShopButton) {
      const inventoryQuantity =
        this.currentVariantData.inventory_quantity ?? 0
      const isPreorder = Boolean(this.preorderText) && inventoryQuantity <= 0
      const variantAvailable = this.currentVariantData.available || isPreorder

      this.setPreorderState(isPreorder)

      if (variantAvailable) {
        this.quickShopButton.removeAttribute('disabled')
        if (isPreorder) {
          this.quickShopButton.innerHTML = 'Pre-order'
        } else {
          this.quickShopButton.innerHTML = `${window.variantStrings.addToCart}`
        }
      } else {
        this.quickShopButton.setAttribute('disabled', 'disabled')
        this.quickShopButton.innerHTML = window.variantStrings.soldOut
      }
    }
  }

  setPreorderState(isPreorder) {
    if (!this.preorderText) return

    this.preorderPropertyInputs.forEach((input) => {
      if (isPreorder) {
        input.name = 'properties[Estimated Shipping]'
        input.value = this.preorderText
      } else {
        input.removeAttribute('name')
        input.value = ''
      }
    })

    this.preorderMessages.forEach((message) => {
      if (isPreorder) {
        message.classList.remove('hidden')
        message.textContent = `Estimated Shipping Date: ${this.preorderText}`
      } else {
        message.classList.add('hidden')
        message.textContent = `Estimated Shipping Date: ${this.preorderText}`
      }
    })
  }

  updateBadge() {
    if (this.badge && this.badge.classList.contains('js-variant-level-badge') && this.currentVariantSupplementData) {
      const badgeData = this.currentVariantSupplementData.badge
      if (!badgeData || !badgeData.text || !badgeData.background_color || !badgeData.font_color) {
        this.badge.innerHTML = ''
        this.badge.style.backgroundColor = ''
        this.badge.style.color = ''
        return
      }
      this.badge.innerHTML = badgeData.text
      this.badge.style.backgroundColor = badgeData.background_color
      this.badge.style.color = badgeData.font_color
    }
  }

  updateColorSwatchLabel(event) {
    if (this.colorSwatchLabel && event?.target?.dataset?.colorTypeValue !== undefined) {
      this.colorSwatchLabel.innerHTML = event.target.dataset.colorTypeValue;
    }
  }

  updateCardPrice() {
    const isCompareAtPriceInvalid =
      !this.currentVariantData.compare_at_price ||
      this.currentVariantData.compare_at_price === '0' ||
      this.currentVariantData.compare_at_price === 0 ||
      this.currentVariantData.compare_at_price === ''

    const isVariantUnavailable = this.currentVariantData.available === false;

    if (isCompareAtPriceInvalid || isVariantUnavailable) {
      for (const priceWrapper of this.cardPriceWrappers) {
        priceWrapper.classList.remove('price--on-sale')
      }
    } else {
      for (const priceWrapper of this.cardPriceWrappers) {
        !priceWrapper.classList.contains('price--on-sale') ? priceWrapper.classList.add('price--on-sale') : null
      }
    }

    for (const priceEl of this.cardPrices) {
      priceEl.innerHTML = window.Shopify.formatMoney(this.currentVariantData.price)
    }

    for (const comparePriceEl of this.cardComparePrices) {
      if (isCompareAtPriceInvalid) {
        comparePriceEl.innerHTML = ''
      } else {
        comparePriceEl.innerHTML = window.Shopify.formatMoney(this.currentVariantData.compare_at_price)
      }
    }
  }
  // Variant switching update END
  // Collapse START
  queryCollapseHeight() {
    const collapseHeight = this.collapse.offsetHeight
    this.style.setProperty('--collapse-height', `${collapseHeight * 2}px`)
  }

  queryCardInfoHeight() {
    const cardInfoHeight = this.cardInfo.offsetHeight
    this.style.setProperty('--card-info-height', `${cardInfoHeight}px`)
  }

  collapseOpen() {
    this.collapse.classList.remove('js-hidden')
    this.collapseOpenState = true

    const event = new CustomEvent('hh-ds-collapse-open', {
      detail: { initiator: this },
      bubbles: true,
      composed: true,
    });
    window.dispatchEvent(event);
  }

  collapseClose() {
    if (!this.collapse.classList.contains('js-hidden')) this.collapse.classList.add('js-hidden')
    this.collapseOpenState = false
  }

  handleDesktopOpen() {
    if (window.innerWidth > 990 && !this.collapseOpenState) {
      this.collapseOpen()
    }
  }

  handleCollapseEvent(event) {
    if (event.detail.initiator !== this) {
      this.collapseClose()
    }
  }
  // Collapse END

  createRenderRoot() {
    return this
  }

  selectFirstAvailableOption() {
    const fieldsets = this.querySelectorAll('fieldset');
    const variantData = this.variantData;
    
    if (!variantData || !Array.isArray(variantData)) {
      console.error('Variant data is not available or invalid.');
      return;
    }

    fieldsets.forEach(fieldset => {
      const inputs = fieldset.querySelectorAll('input[type="radio"]');
      const firstAvailableInput = Array.from(inputs).find(input => {
        // Find a variant that has this option value and is available
        return variantData.some(variant => 
          variant.available && 
          variant.options && 
          Array.isArray(variant.options) && 
          variant.options.includes(input.value)
        );
      });

      if (firstAvailableInput) {
        firstAvailableInput.checked = true;
      } else {
        // If no available option found, select the first option as fallback
        inputs[0].checked = true;
      }
    });

    this.onChangeHandler({ 
      target: this.fieldset,
      dataset: { colorTypeValue: undefined }
    });
  }
}

customElements.define('hh-product-card-ds', HHProductCardDs)
